import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Button, Form } from 'react-bootstrap';
import { Formik, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { toast } from '../../common/Toast/toast';
import { useParams, useNavigate } from 'react-router-dom';
import { getApps } from '../../../../services/AppServices.js';
import '../../common/SettingsStepForm.css';
import {
  createModuleToApp,
  getModuleToAppById,
  updateModuleToApp,
} from '../../../../services/ModulesToAppService.js';
import { getOperationsToModules } from '../../../../services/OMService.js';

/* Step one's rules on their own, and the whole form's built from them,
   so "may Continue?" is answered by the same rules that decide "may
   Save?" -- a hand-written copy of them on the button is a second,
   quieter schema that drifts the moment a rule is added here. */
const stepOneSchema = Yup.object().shape({
  label: Yup.string().trim().required('Label is required'),
  value: Yup.string().trim().required('Value is required'),
  appId: Yup.string().required('App is required'),
});

const validationSchema = stepOneSchema.shape({
  moduleIds: Yup.array().min(1, 'At least one module is required'),
});

/* ── The two steps ──────────────────────────────────────────────────
   Four fields, the last of them a picker long enough to be the whole
   dialog if it shares a screen with the other three. Split the way
   AddAttendance splits its own, and the way the app-to-role and
   operation-to-module dialogs do. The dialog titles itself from this,
   through onStepChange.
   ------------------------------------------------------------------ */
const STEP_COPY = [
  {
    title: 'What is it, and which app?',
    hint: 'A name, the key permission checks are written against, and the application this belongs to.',
  },
  {
    title: 'Which modules does it carry?',
    hint: 'Tick every module this application should carry.',
  },
];

const STEP_LABELS = ['Details', 'Modules'];

// The rule that turns a label into its key. Named because three places
// have to agree on it: the label's onChange, the "use the generated one"
// link, and the check for whether the key is still the derived one.
const slugify = (label) =>
  String(label || '').toLowerCase().trim().replace(/\s+/g, '-');

const idOf = (row) => String(row?._id || row?.id || '');

const TickIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const WarnIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 8v5M12 16.5v.01" />
  </svg>
);

/* The palette's own tokens rather than a second theme of this
   screen's own. These were literal oranges, so these pages and their
   popups stayed orange whichever palette was chosen while the app
   around them moved. ThemeContext writes the --pg-* custom properties
   onto the document, and a custom property works in an inline style
   exactly as it does in a stylesheet, so naming them here is the whole
   of the fix.

   Ink is deliberately not a palette value (see css/theme/palettes.css):
   it holds its contrast under every palette, so it stays literal. */
const THEME = {
  primary: 'var(--pg-accent)',
  primaryDark: 'var(--pg-accent-hover)',
  cardBg: 'var(--pg-card)',
  border: 'var(--pg-line)',
};

const FilterSection = ({ title, children }) => (
  <div
    className="filter-section mb-4 p-4 bg-dark"
    style={{
      borderRadius: '15px',
      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
      // background: THEME.cardBg,
      border: `1px solid ${THEME.border}`,
    }}
  >
    <h5
      className="mb-3"
      style={{
        borderBottom: `2px solid ${THEME.primary}`,
        paddingBottom: '8px',
        color: THEME.primaryDark,
      }}
    >
      {title}
    </h5>
    {children}
  </div>
);

function AddMTApps({
  // Shown inside a dialog rather than as a page: the component drops its
  // own back-link and toast host, since the dialog and the list supply
  // them. The card chrome around it is flattened by SettingsFormModal.css.
  embedded = false,
  // The record to edit. Falls back to the :id route param.
  recordId,
  // Called after a successful save instead of navigating away.
  onDone,
  // Called when the form is dismissed instead of navigating away.
  onCancel,
  // Told the current step's question, so the dialog can title itself.
  onStepChange,
}) {
  const { id: routeId } = useParams();
  const id = recordId ?? routeId;
  const navigate = useNavigate();

  // Leaving the form means "go back to the list" on the route, and "close
  // the dialog" when embedded. Every exit goes through these two so the
  // component never has to know which it is.
  const leave = () => {
    if (onCancel) onCancel();
    else navigate('/setting/mta-list');
  };

  const finish = () => {
    if (onDone) onDone();
    else navigate('/setting/mta-list');
  };
  const [apps, setApps] = useState([]);
  const [modules, setModules] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState({
    label: '',
    value: '',
    appId: '',
    moduleIds: [],
  });

  /* ── Steps ────────────────────────────────────────────────────────
     `step` is the one being asked for; `shownStep` is the one on
     screen, which trails it by one exit. Keying the stage on `step`
     alone swaps the content in a single frame -- the old step vanishes
     with no exit while the new one animates in from nothing, which is
     what reads as a jump. Same pair AddAttendance keeps.
     ---------------------------------------------------------------- */
  const [step, setStep] = useState(1);
  const [shownStep, setShownStep] = useState(1);
  const [stepPhase, setStepPhase] = useState('in');
  // What the picker's search box holds. Lives here rather than in the
  // step so it survives a trip back to step one.
  const [pickQuery, setPickQuery] = useState('');

  useEffect(() => {
    if (shownStep === step) return undefined;
    setStepPhase('out');
    const t = setTimeout(() => {
      setShownStep(step);
      setStepPhase('in');
    }, 160);
    return () => clearTimeout(t);
  }, [step, shownStep]);

  useEffect(() => {
    if (onStepChange) onStepChange(STEP_COPY[step - 1], step);
    // onStepChange is the dialog's setState; adding it here would
    // re-report on every render of the list behind it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch apps and modules
        const [appsRes, modulesRes] = await Promise.all([
          getApps(1000, 1, ''),
          getOperationsToModules(1000, 1, ''),
        ]);

        setApps(appsRes.data || []);
        setModules(modulesRes.data || []);

        // If in edit mode, fetch the existing mapping
        if (id) {
          const mappingRes = await getModuleToAppById(id);
          const mapping = mappingRes.data;

          setInitialValues({
            label: mapping.label || '',
            value: mapping.value || '',
            appId: mapping.appId || '',
            moduleIds: mapping.moduleIds ? mapping.moduleIds.split(',') : [],
          });
          setIsEditMode(true);
        }
      } catch (error) {
        toast.error('Failed to fetch data');
        console.error('Error:', error);
      }
    };

    fetchData();
  }, [id]);

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const payload = {
        label: values.label,
        value: values.value,
        appId: values.appId,
        moduleIds: values.moduleIds.join(','),
      };

      if (isEditMode) {
        await updateModuleToApp(id, payload);
        toast.success('Mapping updated successfully!');
      } else {
        await createModuleToApp(payload);
        toast.success('Mapping created successfully!');
        resetForm();
      }

      finish();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error submitting data');
      console.error('Error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  /* ── The dialog build ───────────────────────────────────────────────
     Two steps, the shape AddAttendance uses and the app-to-role and
     operation-to-module dialogs share. The stepper, the stage and its
     transition are the same `att-*` classes; everything inside a step is
     the `sf-*` language in common/SettingsStepForm.css.
     ------------------------------------------------------------------ */
  if (embedded) {
    return (
      <div className="sf">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, setFieldValue, setFieldTouched, submitForm, isSubmitting, errors, touched }) => {
            const derived = slugify(values.label);
            // The key still matches what the label would generate, so it
            // can go on tracking it. Once it does not, the label stops
            // overwriting it -- a typed key is the reader's, not ours.
            const isAuto = values.value === derived;
            const labelBad = touched.label && errors.label;
            const valueBad = touched.value && errors.value;
            const appBad = touched.appId && errors.appId;
            const modsBad = touched.moduleIds && errors.moduleIds;

            const selected = new Set(values.moduleIds.map(String));
            const shown = pickQuery.trim()
              ? modules.filter((mod) =>
                  String(mod.label || '')
                    .toLowerCase()
                    .includes(pickQuery.trim().toLowerCase())
                )
              : modules;

            const toggle = (mid) => {
              setFieldTouched('moduleIds', true, false);
              setFieldValue(
                'moduleIds',
                selected.has(mid)
                  ? values.moduleIds.filter((v) => String(v) !== mid)
                  : [...values.moduleIds, mid]
              );
            };

            // Bulk actions act on what the search is showing, not on the
            // whole list -- "Select all" under a filter that hides most of
            // it, silently ticking the hidden ones, is a trap.
            const shownIds = shown.map(idOf);
            const allShownOn =
              shownIds.length > 0 && shownIds.every((mid) => selected.has(mid));

            const selectShown = () => {
              setFieldTouched('moduleIds', true, false);
              setFieldValue('moduleIds', [
                ...new Set([...values.moduleIds.map(String), ...shownIds]),
              ]);
            };

            const clearShown = () => {
              setFieldTouched('moduleIds', true, false);
              const drop = new Set(shownIds);
              setFieldValue(
                'moduleIds',
                values.moduleIds.filter((v) => !drop.has(String(v)))
              );
            };

            // Step one is answered when its own fields pass their own
            // rules -- the same object the full schema is built from, so
            // there is no second copy of them here to fall out of step.
            const stepOneOk = stepOneSchema.isValidSync({
              label: values.label,
              value: values.value,
              appId: values.appId,
            });

            const goToTwo = () => {
              setFieldTouched('label', true, true);
              setFieldTouched('value', true, true);
              setFieldTouched('appId', true, true);
              if (stepOneOk) setStep(2);
            };

            const save = () => {
              setFieldTouched('moduleIds', true, true);
              // Saving with a step-one field invalid would fail validation
              // silently -- its error lives on a step that is not on
              // screen. Go back and show it.
              if (!stepOneOk) {
                setStep(1);
                return;
              }
              submitForm();
            };

            return (
              <Form
                noValidate
                onSubmit={(e) => {
                  // Nothing in here is type="submit", so the only thing
                  // that reaches this is Enter pressed inside a field. On
                  // step one that key means "continue".
                  e.preventDefault();
                  if (step !== 2) {
                    goToTwo();
                    return;
                  }
                  save();
                }}
              >
                <ol className="att-steps">
                  {STEP_LABELS.map((label, i) => {
                    const n = i + 1;
                    const state = n === step ? 'is-now' : n < step ? 'is-done' : '';
                    return (
                      <li className={`att-step-tab ${state}`.trim()} key={label}>
                        <button
                          type="button"
                          // Adding: only backwards, a step ahead has not been
                          // answered yet. Editing: every step is filled, so any tab opens.
                          disabled={n === step || (!isEditMode && n > step)}
                          onClick={() => setStep(n)}
                        >
                          <span className="att-step-n">{n}</span>
                          {label}
                        </button>
                      </li>
                    );
                  })}
                </ol>

                {/* Painted from shownStep, not step -- see the comment on
                    the transition effect above. */}
                <div className={`att-stage is-${stepPhase}`}>
                  <div className="att-stage-inner" key={shownStep}>
                    {shownStep === 1 && (
                      <section className="att-step">
                        <p className="sf-step-note">
                          The name is what you will recognise this by; the key is
                          what the code checks.
                        </p>

                        <div className="sf-field">
                          <div className="sf-label-row">
                            <label className="sf-label" htmlFor="mta-label">
                              Label<span className="sf-req">*</span>
                            </label>
                          </div>
                          <input
                            id="mta-label"
                            type="text"
                            className={`sf-input${labelBad ? ' sf-input--bad' : ''}`}
                            value={values.label}
                            placeholder="Reports, Billing, Attendance…"
                            autoComplete="off"
                            onChange={(e) => {
                              const next = e.target.value;
                              setFieldValue('label', next);
                              // Only while the key is still the generated one.
                              if (isAuto) setFieldValue('value', slugify(next));
                            }}
                            onBlur={() => setFieldTouched('label', true, true)}
                          />
                          {labelBad ? (
                            <p className="sf-error"><WarnIcon />{errors.label}</p>
                          ) : (
                            <p className="sf-hint">
                              How this reads wherever permissions are listed.
                            </p>
                          )}
                        </div>

                        <div className="sf-field sf-derived">
                          <div className="sf-label-row">
                            <label className="sf-label" htmlFor="mta-value">
                              Value<span className="sf-req">*</span>
                              {isAuto ? (
                                <span className="sf-state sf-state--auto">From label</span>
                              ) : (
                                <>
                                  <span className="sf-state sf-state--custom">Edited</span>
                                  {derived && (
                                    <button
                                      type="button"
                                      className="sf-relink"
                                      onClick={() => setFieldValue('value', derived)}
                                    >
                                      use {derived}
                                    </button>
                                  )}
                                </>
                              )}
                            </label>
                          </div>
                          <input
                            id="mta-value"
                            type="text"
                            className={`sf-input sf-input--slug${valueBad ? ' sf-input--bad' : ''}`}
                            value={values.value}
                            placeholder="reports"
                            autoComplete="off"
                            spellCheck="false"
                            onChange={(e) =>
                              setFieldValue('value', e.target.value.replace(/\s+/g, '-'))
                            }
                            onBlur={() => setFieldTouched('value', true, true)}
                          />
                          {valueBad ? (
                            <p className="sf-error"><WarnIcon />{errors.value}</p>
                          ) : (
                            <p className="sf-hint">
                              The key permission checks are written against. Changing
                              it on an entry already in use breaks those checks.
                            </p>
                          )}
                        </div>

                        <div className="sf-field">
                          <div className="sf-label-row">
                            <label className="sf-label" htmlFor="mta-app">
                              App<span className="sf-req">*</span>
                            </label>
                          </div>
                          <select
                            id="mta-app"
                            className={`sf-input${appBad ? ' sf-input--bad' : ''}`}
                            value={values.appId}
                            onChange={(e) => setFieldValue('appId', e.target.value)}
                            onBlur={() => setFieldTouched('appId', true, true)}
                          >
                            <option value="">Select an app…</option>
                            {apps.map((app) => (
                              <option key={idOf(app)} value={idOf(app)}>
                                {app.label}
                              </option>
                            ))}
                          </select>
                          {appBad ? (
                            <p className="sf-error"><WarnIcon />{errors.appId}</p>
                          ) : (
                            <p className="sf-hint">
                              The application these modules are carried by.
                            </p>
                          )}
                        </div>
                      </section>
                    )}

                    {shownStep === 2 && (
                      <section className="att-step">
                        <p className="sf-step-note">
                          Every module ticked here is carried by the app chosen in
                          the previous step.
                        </p>

                        <div className="sf-pick-bar">
                          <div className="sf-search">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                              <circle cx="11" cy="11" r="7" />
                              <path d="m20 20-3.5-3.5" />
                            </svg>
                            <input
                              type="text"
                              className="sf-search-input"
                              value={pickQuery}
                              placeholder="Search modules…"
                              autoComplete="off"
                              onChange={(e) => setPickQuery(e.target.value)}
                            />
                          </div>

                          <button
                            type="button"
                            className="sf-bulk"
                            onClick={allShownOn ? clearShown : selectShown}
                            disabled={shownIds.length === 0}
                          >
                            {allShownOn ? 'Clear' : 'Select'}
                            {pickQuery.trim() ? ' shown' : ' all'}
                          </button>
                        </div>

                        <p className="sf-tally">
                          <b>{values.moduleIds.length}</b> of {modules.length} selected
                          {pickQuery.trim() && ` · ${shown.length} shown`}
                        </p>

                        {shown.length === 0 ? (
                          <div className="sf-empty">
                            {modules.length === 0
                              ? 'No modules exist yet.'
                              : `Nothing matches “${pickQuery.trim()}”.`}
                          </div>
                        ) : (
                          <ul className="sf-picklist">
                            {shown.map((mod) => {
                              const mid = idOf(mod);
                              const on = selected.has(mid);
                              return (
                                <li key={mid}>
                                  <button
                                    type="button"
                                    className={`sf-pick${on ? ' is-on' : ''}`}
                                    aria-pressed={on}
                                    onClick={() => toggle(mid)}
                                  >
                                    <span className="sf-box" aria-hidden="true">
                                      <TickIcon />
                                    </span>
                                    <span className="sf-pick-name">{mod.label}</span>
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        )}

                        {modsBad && (
                          <p className="sf-error"><WarnIcon />{errors.moduleIds}</p>
                        )}
                      </section>
                    )}
                  </div>
                </div>

                <footer className="sf-foot">
                  {/* Keyed, and neither is type="submit". Two buttons at one
                      position are otherwise reconciled onto a single DOM
                      node: clicking Continue would flush step=2 before the
                      browser reached the click's default action, by which
                      time that same node was a submit button inside a form
                      -- and the record saved from step one. */}
                  {step > 1 ? (
                    <button key="back" type="button" className="sf-btn" onClick={() => setStep(1)}>
                      Back
                    </button>
                  ) : (
                    <button key="cancel" type="button" className="sf-btn" onClick={leave}>
                      Cancel
                    </button>
                  )}

                  <span className="sf-foot-gap" />

                  {step === 1 ? (
                    <button
                      key="continue"
                      type="button"
                      className="sf-btn sf-btn--main"
                      onClick={goToTwo}
                      disabled={!stepOneOk}
                    >
                      Continue
                    </button>
                  ) : (
                    <button
                      key="save"
                      type="button"
                      className="sf-btn sf-btn--main"
                      disabled={isSubmitting || values.moduleIds.length === 0}
                      onClick={save}
                    >
                      {isSubmitting && <span className="sf-spin" aria-hidden="true" />}
                      {isSubmitting
                        ? isEditMode ? 'Updating…' : 'Saving…'
                        : isEditMode ? 'Update mapping' : 'Create mapping'}
                    </button>
                  )}
                </footer>
              </Form>
            );
          }}
        </Formik>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      {/* Page-only: the list behind the dialog already has a toast host,
          and two would show every message twice. */}
      <Card className="border-0 shadow-sm" style={{ borderRadius: '15px', overflow: 'hidden' }}>
        <Card.Body className='bg-dark'>
          <FilterSection title={isEditMode ? 'Edit App Mapping' : 'Add App Mapping'}>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {(formik) => {
                const { values, setFieldValue, isSubmitting, errors, touched } = formik;

                const toggleModule = (modId) => {
                  if (values.moduleIds.includes(modId)) {
                    setFieldValue(
                      'moduleIds',
                      values.moduleIds.filter((v) => v !== modId)
                    );
                  } else {
                    setFieldValue('moduleIds', [...values.moduleIds, modId]);
                  }
                };

                const selectAll = () =>
                  setFieldValue(
                    'moduleIds',
                    modules.map((m) => m._id || m.id)
                  );
                const clearAll = () => setFieldValue('moduleIds', []);

                return (
                  <Form onSubmit={formik.handleSubmit}>
                    <Row className="mb-3">
                      <Col md={6}>
                        <Form.Group controlId="formLabel">
                          <Form.Label style={{ color: THEME.primaryDark }}>
                            Label <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="label"
                            value={values.label}
                            onChange={(e) => {
                              const label = e.target.value;
                              const processed = label.toLowerCase().replace(/\s+/g, '-');
                              setFieldValue('label', label);
                              setFieldValue('value', processed);
                            }}
                            placeholder="Enter label"
                            isInvalid={touched.label && errors.label}
                          />
                          <ErrorMessage name="label" component="div" className="text-danger" />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="formValue">
                          <Form.Label style={{ color: THEME.primaryDark }}>
                            Value <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="value"
                            value={values.value}
                            onChange={(e) =>
                              setFieldValue('value', e.target.value.replace(/\s+/g, '-'))
                            }
                            placeholder="Enter value"
                            isInvalid={touched.value && errors.value}
                          />
                          <ErrorMessage name="value" component="div" className="text-danger" />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="mb-3">
                      <Col md={6}>
                        <Form.Group controlId="formAppId">
                          <Form.Label style={{ color: THEME.primaryDark }}>
                            Apps <span className="text-danger">*</span>
                          </Form.Label>
                          <div className="d-flex flex-wrap gap-2">
                            {apps.map((app) => (
                              <Button
                                key={app._id || app.id}
                                variant={
                                  values.appId === (app._id || app.id)
                                    ? 'warning'
                                    : 'outline-warning'
                                }
                                style={{
                                  borderRadius: '20px',
                                  fontWeight: 500,
                                  minWidth: 180,
                                  marginBottom: 8,
                                }}
                                onClick={() => setFieldValue('appId', app._id || app.id)}
                                type="button"
                              >
                                {app.label}
                              </Button>
                            ))}
                          </div>
                          <ErrorMessage name="appId" component="div" className="text-danger" />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="mb-3">
                      <Col md={12}>
                        <Form.Group controlId="formModuleIds">
                          <Form.Label style={{ color: THEME.primaryDark }}>
                            Modules <span className="text-danger">*</span>
                          </Form.Label>
                          <div className="mb-2 d-flex gap-2">
                            <Button
                              variant="outline-warning"
                              size="sm"
                              onClick={selectAll}
                              style={{ borderRadius: '20px', fontWeight: 500 }}
                              type="button"
                            >
                              Select All
                            </Button>
                            <Button
                              variant="outline-dark"
                              size="sm"
                              onClick={clearAll}
                              style={{ borderRadius: '20px', fontWeight: 500 }}
                              type="button"
                            >
                              Clear All
                            </Button>
                          </div>
                          <div className="d-flex flex-wrap gap-3">
                            {modules.map((mod) => (
                              <div
                                key={mod._id || mod.id}
                                className={`d-flex align-items-center px-3 py-2 rounded border bg-white text-blackk`}
                                style={{
                                  minWidth: 220,
                                  marginBottom: 8,
                                  borderWidth: 2,
                                  cursor: 'pointer',
                                  transition: 'background 0.2s',
                                }}
                                onClick={() => toggleModule(mod._id || mod.id)}
                              >
                                <Form.Check
                                  type="switch"
                                  id={`mod-switch-${mod._id || mod.id}`}
                                  checked={values.moduleIds.includes(mod._id || mod.id)}
                                  onChange={() => toggleModule(mod._id || mod.id)}
                                  className="me-2"
                                  style={{ pointerEvents: 'none' }}
                                />
                                <span style={{ fontWeight: 500, color: '#444' }}>{mod.label}</span>
                              </div>
                            ))}
                          </div>
                          <div className="text-muted mt-1" style={{ fontSize: 13 }}>
                            Select modules that will be assigned to this app
                          </div>
                          <ErrorMessage name="moduleIds" component="div" className="text-danger" />
                        </Form.Group>
                      </Col>
                    </Row>
                    <div className="d-flex justify-content-end gap-3 mt-4">
                      <Button
                        variant="secondary"
                        onClick={leave}
                        style={{
                          borderRadius: '30px',
                          padding: '8px 30px',
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="success"
                        type="submit"
                        disabled={isSubmitting}
                        style={{
                          backgroundColor: THEME.primary,
                          borderColor: THEME.primary,
                          borderRadius: '30px',
                          padding: '8px 30px',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                        }}
                      >
                        {isSubmitting ? 'Processing...' : isEditMode ? 'Update' : 'Save'}
                      </Button>
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </FilterSection>
        </Card.Body>
      </Card>
    </div>
  );
}

export default AddMTApps;
